import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { reviewsApi, usersApi, departmentsApi, businessUnitsApi, divisionsApi, gradesApi, categoriesApi } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { SearchableSelect } from '../components/ui/searchable-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Save, CheckCircle, PenTool, Users, Filter, X } from 'lucide-react';

export default function CalibrationPage() {
  const { user, isHR, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [grades, setGrades] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedReview, setSelectedReview] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [calibratedRating, setCalibratedRating] = useState('');
  const [hrNotes, setHrNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [signatureType, setSignatureType] = useState('');
  const [signatureName, setSignatureName] = useState('');
  
  // Filter states
  const [filterEmployeeId, setFilterEmployeeId] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterUnit, setFilterUnit] = useState('all');
  const [filterDivision, setFilterDivision] = useState('all');
  const [filterGrade, setFilterGrade] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      const [reviewsRes, usersRes, deptsRes, busRes, divRes, gradeRes, catRes] = await Promise.all([
        reviewsApi.getAll({ year: currentYear }),
        usersApi.getAll(),
        departmentsApi.getAll(),
        businessUnitsApi.getAll(),
        divisionsApi.getAll(),
        gradesApi.getAll(),
        categoriesApi.getAll(),
      ]);
      
      // Filter reviews that have completed year-end review (have goal_score or overall_rating)
      // These are ready for calibration
      const calibrationReadyReviews = reviewsRes.data.filter(r => 
        r.goal_score !== null && r.goal_score !== undefined
      );
      
      setReviews(calibrationReadyReviews);
      setUsers(usersRes.data);
      setDepartments(deptsRes.data || []);
      setBusinessUnits(busRes.data || []);
      setDivisions(divRes.data || []);
      setGrades(gradeRes.data || []);
      setCategories(catRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getUserName = (employeeId) => {
    const u = users.find(u => u.id === employeeId);
    return u?.name || 'Unknown';
  };

  const getEmployeeNumber = (employeeId) => {
    const u = users.find(u => u.id === employeeId);
    return u?.employee_number || '-';
  };
  
  const getUserData = (employeeId) => {
    return users.find(u => u.id === employeeId) || {};
  };

  // Filter reviews based on user data
  const filteredReviews = reviews.filter(review => {
    const userData = getUserData(review.employee_id);
    
    if (filterEmployeeId && !userData.employee_number?.toLowerCase().includes(filterEmployeeId.toLowerCase())) {
      return false;
    }
    if (filterDepartment !== 'all' && userData.department !== filterDepartment) {
      return false;
    }
    if (filterUnit !== 'all' && userData.business_unit !== filterUnit) {
      return false;
    }
    if (filterDivision !== 'all' && userData.division !== filterDivision) {
      return false;
    }
    if (filterGrade !== 'all' && userData.grade !== filterGrade) {
      return false;
    }
    if (filterCategory !== 'all' && userData.category !== filterCategory) {
      return false;
    }
    return true;
  });

  const clearFilters = () => {
    setFilterEmployeeId('');
    setFilterDepartment('all');
    setFilterUnit('all');
    setFilterDivision('all');
    setFilterGrade('all');
    setFilterCategory('all');
  };

  const hasActiveFilters = filterEmployeeId || filterDepartment !== 'all' || filterUnit !== 'all' || 
    filterDivision !== 'all' || filterGrade !== 'all' || filterCategory !== 'all';

  const handleOpenCalibration = (review) => {
    setSelectedReview(review);
    setCalibratedRating(review.calibrated_rating?.toString() || '');
    setHrNotes(review.hr_notes || '');
    setDialogOpen(true);
  };

  const handleSaveCalibration = async () => {
    if (!selectedReview) return;
    
    setSaving(true);
    try {
      await reviewsApi.calibrate(selectedReview.id, {
        rating: parseFloat(calibratedRating),
        notes: hrNotes,
      });
      toast.success('Calibration saved');
      setDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save calibration');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenSignature = (review, type) => {
    setSelectedReview(review);
    setSignatureType(type);
    setSignatureName('');
    setSignDialogOpen(true);
  };

  const handleSign = async () => {
    if (!selectedReview || !signatureName.trim()) {
      toast.error('Please enter your name to sign');
      return;
    }
    
    setSaving(true);
    try {
      await reviewsApi.sign(selectedReview.id, {
        type: signatureType,
        signature: signatureName,
      });
      toast.success('Signature recorded');
      setSignDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Failed to sign:', error);
      toast.error('Failed to record signature');
    } finally {
      setSaving(false);
    }
  };

  const getRatingBadge = (rating) => {
    if (!rating) return <Badge variant="outline">-</Badge>;
    
    const colors = {
      5: 'bg-teal-100 text-teal-800',
      4: 'bg-green-100 text-green-800',
      3: 'bg-yellow-100 text-yellow-800',
      2: 'bg-orange-100 text-orange-800',
      1: 'bg-red-100 text-red-800',
    };
    
    return (
      <Badge className={colors[Math.round(rating)] || 'bg-gray-100'}>
        {rating.toFixed(1)}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="calibration-page">
      <div>
        <h1 className="text-3xl font-bold">Calibration</h1>
        <p className="text-muted-foreground mt-1">
          Review and calibrate performance ratings
        </p>
      </div>

      {/* Filters Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="ml-auto h-7 text-xs">
                <X className="h-3 w-3 mr-1" />
                Clear All
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Employee ID</Label>
              <Input
                placeholder="Search..."
                value={filterEmployeeId}
                onChange={(e) => setFilterEmployeeId(e.target.value)}
                className="h-9 mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Department</Label>
              <SearchableSelect
                value={filterDepartment}
                onValueChange={setFilterDepartment}
                placeholder="All"
                searchPlaceholder="Search departments..."
                className="h-9 mt-1"
                options={[
                  { value: "all", label: "All Departments" },
                  ...departments.map(d => ({ value: d.name, label: d.name })),
                ]}
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Unit</Label>
              <SearchableSelect
                value={filterUnit}
                onValueChange={setFilterUnit}
                placeholder="All"
                searchPlaceholder="Search units..."
                className="h-9 mt-1"
                options={[
                  { value: "all", label: "All Units" },
                  ...businessUnits.map(bu => ({ value: bu.name, label: bu.name })),
                ]}
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Division</Label>
              <SearchableSelect
                value={filterDivision}
                onValueChange={setFilterDivision}
                placeholder="All"
                searchPlaceholder="Search divisions..."
                className="h-9 mt-1"
                options={[
                  { value: "all", label: "All Divisions" },
                  ...divisions.map(d => ({ value: d.name, label: d.name })),
                ]}
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Grade</Label>
              <SearchableSelect
                value={filterGrade}
                onValueChange={setFilterGrade}
                placeholder="All"
                searchPlaceholder="Search grades..."
                className="h-9 mt-1"
                options={[
                  { value: "all", label: "All Grades" },
                  ...grades.map(g => ({ value: g.name, label: g.name })),
                ]}
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Category</Label>
              <SearchableSelect
                value={filterCategory}
                onValueChange={setFilterCategory}
                placeholder="All"
                searchPlaceholder="Search categories..."
                className="h-9 mt-1"
                options={[
                  { value: "all", label: "All Categories" },
                  ...categories.map(c => ({ value: c.name, label: c.name })),
                ]}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Year-End Reviews ({currentYear})
            <Badge variant="outline" className="ml-2">{filteredReviews.length} of {reviews.length}</Badge>
          </CardTitle>
          <CardDescription>
            Calibrate ratings and collect signatures
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredReviews.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              {reviews.length === 0 ? 'No year-end reviews found for calibration' : 'No reviews match the selected filters'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead className="text-center">Goal Score</TableHead>
                  <TableHead className="text-center">Competency Score</TableHead>
                  <TableHead className="text-center">Calibrated Rating</TableHead>
                  <TableHead className="text-center">Manager Sign</TableHead>
                  <TableHead className="text-center">Employee Sign</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReviews.map((review) => (
                  <TableRow key={review.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{getUserName(review.employee_id)}</p>
                        <p className="text-xs text-muted-foreground">{getEmployeeNumber(review.employee_id)}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {getRatingBadge(review.goal_score)}
                    </TableCell>
                    <TableCell className="text-center">
                      {getRatingBadge(review.competency_score)}
                    </TableCell>
                    <TableCell className="text-center">
                      {getRatingBadge(review.calibrated_rating)}
                    </TableCell>
                    <TableCell className="text-center">
                      {review.manager_signature ? (
                        <div className="flex items-center justify-center gap-1 text-green-600">
                          <CheckCircle className="h-4 w-4" />
                          <span className="text-xs">{review.manager_signature}</span>
                        </div>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenSignature(review, 'manager')}
                          data-testid={`manager-sign-${review.id}`}
                        >
                          <PenTool className="h-4 w-4 mr-1" />
                          Sign
                        </Button>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {review.employee_signature ? (
                        <div className="flex items-center justify-center gap-1 text-green-600">
                          <CheckCircle className="h-4 w-4" />
                          <span className="text-xs">{review.employee_signature}</span>
                        </div>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenSignature(review, 'employee')}
                          data-testid={`employee-sign-${review.id}`}
                        >
                          <PenTool className="h-4 w-4 mr-1" />
                          Sign
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={review.status === 'completed' ? 'default' : 'outline'}>
                        {review.status === 'completed' ? 'Completed' : 'In Progress'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {(isHR || isAdmin) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenCalibration(review)}
                          data-testid={`calibrate-${review.id}`}
                        >
                          Calibrate
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Calibration Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Calibrate Rating</DialogTitle>
            <DialogDescription>
              Enter the calibrated rating for {selectedReview && getUserName(selectedReview.employee_id)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Calibrated Rating (1-5)</Label>
              <Input
                type="number"
                min="1"
                max="5"
                step="0.1"
                value={calibratedRating}
                onChange={(e) => setCalibratedRating(e.target.value)}
                placeholder="Enter rating"
                data-testid="calibrated-rating-input"
              />
            </div>
            <div className="space-y-2">
              <Label>HR Notes</Label>
              <Textarea
                value={hrNotes}
                onChange={(e) => setHrNotes(e.target.value)}
                placeholder="Add notes about the calibration decision..."
                rows={4}
                data-testid="hr-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCalibration} disabled={saving} data-testid="save-calibration-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Calibration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signature Dialog */}
      <Dialog open={signDialogOpen} onOpenChange={setSignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Digital Signature</DialogTitle>
            <DialogDescription>
              Enter your name to sign the calibrated rating as {signatureType}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input
                value={signatureName}
                onChange={(e) => setSignatureName(e.target.value)}
                placeholder="Type your full name to sign"
                data-testid="signature-name-input"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              By typing your name, you acknowledge that you have reviewed and agree with the calibrated rating.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSign} disabled={saving || !signatureName.trim()} data-testid="confirm-signature-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <PenTool className="h-4 w-4 mr-2" />}
              Sign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
